import { useEffect, useMemo, useState } from 'react';

export const IOS_FULLSCREEN_CLASS = 'fullscreen-ios-fallback';

/** Use when checking fullscreen state so iOS fallback is included (e.g. in fullscreenchange handlers). */
export function getFullscreenElement(): Element | null | undefined {
  const doc = document as Document & {
    fullscreenElement?: Element | null;
    webkitFullscreenElement?: Element | null;
  };
  if (doc.fullscreenElement ?? doc.webkitFullscreenElement)
    return doc.fullscreenElement ?? doc.webkitFullscreenElement;
  if (document.documentElement.classList.contains(IOS_FULLSCREEN_CLASS))
    return document.documentElement;
  return null;
}

/** Detect iOS (iPhone/iPad). iPad on iOS 13+ may report as MacIntel. */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return (
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

function shouldUseIOSFallback(): boolean {
  // iOS page fullscreen is unreliable via the Fullscreen API; use CSS fallback.
  return isIOS();
}

function injectIOSFullscreenStyles(): void {
  if (document.getElementById('fullscreen-ios-fallback-styles')) return;
  const style = document.createElement('style');
  style.id = 'fullscreen-ios-fallback-styles';
  style.textContent = `
    html.${IOS_FULLSCREEN_CLASS} {
      height: 100%;
      overflow: hidden;
    }
    html.${IOS_FULLSCREEN_CLASS} body {
      position: fixed;
      inset: 0;
      width: 100%;
      height: 100%;
      height: 100dvh;
      overflow: hidden;
      -webkit-overflow-scrolling: touch;
    }
  `;
  document.head.appendChild(style);
}

export function enterFullscreen(): void {
  if (shouldUseIOSFallback()) {
    injectIOSFullscreenStyles();
    document.documentElement.classList.add(IOS_FULLSCREEN_CLASS);
    document.dispatchEvent(new Event('fullscreenchange'));
    return;
  }

  const elem = document.documentElement as HTMLElement & {
    requestFullscreen?: () => Promise<void>;
    mozRequestFullScreen?: () => void;
    webkitRequestFullscreen?: (flags?: number) => void;
    msRequestFullscreen?: () => void;
  };
  if (elem.requestFullscreen) {
    void elem.requestFullscreen().catch(() => {});
  } else if (elem.mozRequestFullScreen) {
    elem.mozRequestFullScreen();
  } else if (elem.webkitRequestFullscreen) {
    elem.webkitRequestFullscreen(
      (Element as unknown as { ALLOW_KEYBOARD_INPUT: number })
        .ALLOW_KEYBOARD_INPUT,
    );
  } else if (elem.msRequestFullscreen) {
    elem.msRequestFullscreen();
  }
}

export function exitFullscreen(): void {
  if (shouldUseIOSFallback()) {
    document.documentElement.classList.remove(IOS_FULLSCREEN_CLASS);
    document.dispatchEvent(new Event('fullscreenchange'));
    return;
  }

  const doc = document as Document & {
    exitFullscreen?: () => Promise<void>;
    mozCancelFullScreen?: () => void;
    webkitExitFullscreen?: () => void;
    msExitFullscreen?: () => void;
  };
  if (doc.exitFullscreen) {
    void doc.exitFullscreen().catch(() => {});
  } else if (doc.mozCancelFullScreen) {
    doc.mozCancelFullScreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  } else if (doc.msExitFullscreen) {
    doc.msExitFullscreen();
  }
}

export function isFullscreenActive(): boolean {
  return getFullscreenElement() != null;
}

export const useFullScreen = () => {
  const [fullScreen, setFullScreen] = useState(0);

  const useIOSFallback = useMemo(() => shouldUseIOSFallback(), []);

  useEffect(() => {
    if (fullScreen === 0) return;

    const active = isFullscreenActive();

    if (fullScreen === 1 && !active) {
      enterFullscreen();
    } else if (fullScreen === -1 && active) {
      exitFullscreen();
    }
  }, [fullScreen, useIOSFallback]);

  return { fullScreen, setFullScreen, enterFullscreen, exitFullscreen };
};
